import common from "../common";
import GelScene from "../ui/gel/GelScene";
import screenfull from "screenfull";

export default class PreloaderScene extends GelScene {
    constructor(first = false) {
        super();

        this.loaded = 0;

        this._bindings = {};
        this._first = first;

        this._bg = null;
        this._title = null;
        this._dummyLabel = null;

        this._progress = null;
        this._tip = null;
        this._isLoaded = false;

        this._bindings.progress = P3.assets.signals.progress.add((event) => this.onLoadingProgress(event));
        this._bindings.complete = P3.assets.signals.complete.once((event) => this.onLoadingComplete(event));
    }

    init() {
        super.init();

        this._bg = new PIXI.Sprite(P3.assets.texture("ui_preloader"));
        this._bg.width = common.STAGE_WIDTH;
        this.addChild(this._bg);

        this._title = new PIXI.Sprite(P3.assets.texture("ui_logo"));
        this._title.position.set(common.STAGE_WIDTH * 0.5, 300);
        this._title.anchor.set(0.5);
        this._title.visible = false;
        this.addChild(this._title);

        this._dummyLabel = new PIXI.Text("", {fontFamily: "BBC Reith Sans"});
        this.addChild(this._dummyLabel);

        this.initProgressBar();
        this.initTipPanel();
    }

    destroy() {
        this._bindings.progress && this._bindings.progress.detach();
        this._bindings.complete && this._bindings.complete.detach();

        // clean up goes here
        super.destroy();
    }

    appear()
    {
        super.appear();

        if (common.requiredAssetsReady && !this._isLoaded)
        {
            this.onLoadingComplete(null);
        }
    }

    animateIn() {
        if (this.parent === null) return;

        this._title.alpha = 0;
        new P3.utils.TWEEN.Tween(this._title, this._tweens)
            .to({alpha: 1}, 200)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(200)
            .onStart(() => {
                this._title.visible = true;
            })
            .start();

        let to = this._title.y;
        this._title.y += 80;
        new P3.utils.TWEEN.Tween(this._title, this._tweens)
            .to({y: to}, 340)
            .easing(P3.utils.TWEEN.Easing.Cubic.Out)
            .delay(200)
            .start();

        this._title.scale.set(0.5);
        new P3.utils.TWEEN.Tween(this._title.scale, this._tweens)
            .to({x: 1, y: 1}, 340)
            .easing(P3.utils.TWEEN.Easing.Back.Out)
            .delay(200)
            .start();

        this._progress.alpha = 0;
        new P3.utils.TWEEN.Tween(this._progress, this._tweens)
            .to({alpha: 1}, 240)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(400)
            .onStart(() => {
                this._progress.visible = true;
            })
            .start();

        to = this._tip.y;
        this._tip.y += 100;
        new P3.utils.TWEEN.Tween(this._tip, this._tweens)
            .to({y: to}, 400)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .delay(1000)
            .onStart(() => {
                this._tip.visible = true;
            })
            .start();
    }

    animateOut(callback, scope) {
        new P3.utils.TWEEN.Tween(this._progress, this._tweens)
            .to({alpha: 0}, 240)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .onComplete(() => {
                this._progress.visible = false;
            })
            .start();

        let to = this._title.y - 60;
        new P3.utils.TWEEN.Tween(this._title, this._tweens)
            .to({y: to}, 460)
            .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
            .delay(200)
            .onComplete(() => {
                callback && callback.call(scope);
            })
            .start();
    }

    update() {
        super.update();
        this._progress.fill.mask.scale.x = this.loaded;
    }

    initProgressBar() {
        this._progress = new PIXI.Sprite(P3.assets.texture("ui_preloader_bg"));
        this._progress.position.set(common.STAGE_WIDTH * 0.5, 600);
        this._progress.anchor.set(0.5);
        this._progress.visible = false;
        this.addChild(this._progress);

        this._progress.fill = new PIXI.Sprite(P3.assets.texture("ui_preloader_fill"));
        this._progress.fill.anchor.set(0.5);
        this._progress.addChild(this._progress.fill);

        const width = this._progress.fill.texture.width;
        const height = this._progress.fill.texture.height;

        this._progress.fill.mask = new PIXI.Graphics();
        this._progress.fill.mask.position.set(-width * 0.5, 0);
        this._progress.fill.mask.scale.x = 0;
        this._progress.fill.mask.beginFill(0xFF0000, 1);
        this._progress.fill.mask.drawRect(0, -height * 0.5, width, height);
        this._progress.fill.mask.endFill();
        this._progress.fill.addChild(this._progress.fill.mask);

        this._progress.label = new PIXI.extras.BitmapText("Loading", {font: "34px BBC Reith Sans"});
        this._progress.label.position.set(-4, 22);
        this._progress.label.anchor.set(0.5);
        this._progress.label.tint = 0xF16751;
        this._progress.addChild(this._progress.label);
    }

    initTipPanel() {
        this._tip = new PIXI.Container();
        this._tip.position.set(common.STAGE_WIDTH * 0.5, P3.View.height - 28);
        this._tip.visible = false;
        this.addChild(this._tip);

        this._tip.image = new PIXI.mesh.NineSlicePlane(P3.assets.texture("answer_box_9slice"), 88, 44, 88, 44);
        this._tip.image.width = 800;
        this._tip.image.pivot.set(this._tip.image.width * 0.5, this._tip.image.height * 0.5);
        this._tip.addChild(this._tip.image);

        this._tip.label = new PIXI.extras.BitmapText("Have your brain, pencil and paper ready!", {font: "34px BBC Reith Sans"});
        this._tip.label.position.set(0, -10);
        this._tip.label.anchor.set(0.5);
        this._tip.label.tint = 0x2B365A;
        this._tip.addChild(this._tip.label);
    }

    onLoadingProgress(event) {
        this.loaded = event.progress / 100;
        console.log("loading:", event.progress);
    }

    onLoadingComplete(event) {
        this.loaded = 1;
        this._isLoaded = true;

        this._bindings.progress.detach();
        this._bindings.complete.detach();

        this._bindings.progress = null;
        this._bindings.complete = null;

        if (this._first) {
            this.animateOut(() => {
                this.initPlayButton();
            });
        } else {
            P3.utils.delayg(this._tweens, 2, () => {
                this.signals.next.dispatch(this);
            });
        }
    }

    onPlayButtonClick(button) {
        this.signals.next.dispatch(this);

        if (PIXI.utils.isMobile.android.device && screenfull.enabled) {
            screenfull.request();
        }
    }
}